import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { onMessage, getMessaging } from "firebase/messaging";
import { app } from "../firebase";
import { addNotification } from "../store/notifications.slice";
import { isSupported } from "firebase/messaging";
import { shouldSuppressNotification } from "../utils/notificationFilters";
import * as Sentry from "@sentry/react";

/**
 * Check if the orders page is currently active
 * Orders page includes: /orders and /orders/:externalOrderId
 * This is used to suppress push notifications when SSE is handling order updates
 */
const isOrdersPageActive = (): boolean => {
  const pathname = window.location.pathname;
  // Match /orders or /orders?... or /orders/:externalOrderId
  return pathname === '/orders' ||
    pathname.startsWith('/orders?') ||
    /^\/orders\/[^/]+$/.test(pathname);
};

export const useNotificationListener = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let serviceWorkerMessageHandler: ((event: MessageEvent) => void) | null =
      null;

    const handleNotification = (
      title: string,
      body: string,
      data?: Record<string, any>
    ) => {
      // Suppress offline order notifications
      if (shouldSuppressNotification(data)) {
        return;
      }

      // Suppress order notifications when orders page is active (SSE will handle them)
      // This ensures SSE takes precedence over push notifications when active
      // Push notifications will still work when:
      // 1. User is not on orders page (SSE not active)
      // 2. SSE connection fails (graceful degradation)
      // 3. Notification is not order-related (type !== 'order')
      if (data?.type === 'order' && isOrdersPageActive()) {
        // SSE is handling this notification - suppress push notification
        return;
      }

      // For all other cases, dispatch notification normally
      // This allows parallel operation: SSE for orders page, push for everything else
      // Badge management: addNotification increments unreadCount in notifications slice
      // The useAppBadge hook (in App.tsx) syncs the badge with unreadCount
      dispatch(
        addNotification({
          title,
          body,
          data,
        })
      );
    };

    const setupListener = async () => {
      try {
        if ("serviceWorker" in navigator) {
          try {
            await navigator.serviceWorker.ready;
          } catch (error) {
            Sentry.captureException(error, {
              level: "warning",
              tags: { feature: "service_worker" },
            });
          }

          serviceWorkerMessageHandler = (event: MessageEvent) => {
            let messageData = event.data;

            if (typeof messageData === "string") {
              try {
                messageData = JSON.parse(messageData);
              } catch (e) {
                Sentry.captureException(e, {
                  level: "warning",
                  tags: { feature: "notification_parse" },
                });
              }
            }

            // Handle navigation messages from service worker
            if (messageData && messageData.type === "NAVIGATE") {
              if (messageData.url) {
                navigate(messageData.url);
              }
              return;
            }

            if (messageData && messageData.type === "PUSH_NOTIFICATION") {
              const payload = messageData.payload;

              const title =
                payload?.title ||
                payload?.notification?.title ||
                payload?.data?.title ||
                messageData?.title ||
                "New Notification";

              const body =
                payload?.body ||
                payload?.notification?.body ||
                payload?.data?.body ||
                messageData?.body ||
                "";

              handleNotification(
                title,
                body,
                payload?.data || messageData?.data
              );
            } else if (
              messageData &&
              (messageData.notification || messageData.title)
            ) {
              const title =
                messageData.notification?.title ||
                messageData.title ||
                messageData.data?.title ||
                "New Notification";

              const body =
                messageData.notification?.body ||
                messageData.body ||
                messageData.data?.body ||
                "";

              handleNotification(title, body, messageData.data);
            }
          };

          navigator.serviceWorker.addEventListener(
            "message",
            serviceWorkerMessageHandler
          );

          if (!navigator.serviceWorker.controller) {
            navigator.serviceWorker.addEventListener("controllerchange", () => {
              // Service worker controller changed
            });
          }
        }

        try {
          const supported = await isSupported();

          if (supported) {
            const messaging = getMessaging(app);

            unsubscribe = onMessage(messaging, (payload) => {
              const title =
                payload.notification?.title ||
                payload.data?.title ||
                "New Notification";

              const body =
                payload.notification?.body || payload.data?.body || "";

              handleNotification(title, body, payload.data);
            });
          }
        } catch (error) {
          Sentry.captureException(error, {
            level: "warning",
            tags: { feature: "fcm_setup" },
          });
        }
      } catch (error) {
        Sentry.captureException(error, {
          level: "warning",
          tags: { feature: "notification_listener" },
        });
      }
    };

    setupListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (serviceWorkerMessageHandler && "serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "message",
          serviceWorkerMessageHandler
        );
      }
    };
  }, [dispatch, navigate]);
};
